import axios, { isAxiosError } from 'axios';

import { DEFAULT_EXPORT_LANG, ExportEntityType, ExportParams, ExportedFile } from '@/module/Export/model/ExportTypes';

import { getContentDispositionFilename, saveBlob } from '@feature/utils';

export function getExportFallbackFilename(entityType: ExportEntityType, params: ExportParams) {
  return `${entityType}_${params.lang ?? DEFAULT_EXPORT_LANG}.xlsx`;
}

/**
 * Only sends the filters that are actually set - an empty `validationStatus=` / `year=` would be
 * rejected by the backend instead of meaning "all".
 */
function toQueryParams(params: ExportParams) {
  const { lang, validationStatus, year } = params;

  // `qs` only skips null / undefined, so empty strings and NaN have to be dropped here
  const hasYear = year != null && String(year).trim() !== '' && Number.isFinite(Number(year));

  return {
    ...(lang ? { lang } : {}),
    ...(validationStatus ? { validationStatus } : {}),
    ...(hasYear ? { year: Number(year) } : {}),
  };
}

export function exportEntity(entityType: ExportEntityType, params: ExportParams = {}): Promise<ExportedFile> {
  return axios
    .get<Blob>(`/export/${entityType}`, {
      responseType: 'blob',
      params: toQueryParams(params),
    })
    .then(response => ({
      blob: response.data,
      filename:
        getContentDispositionFilename(response.headers?.['content-disposition']) ??
        getExportFallbackFilename(entityType, params),
    }));
}

/** Fetches the export and hands it to the browser as a download. */
export async function downloadEntityExport(
  entityType: ExportEntityType,
  params: ExportParams = {},
): Promise<ExportedFile> {
  const exported = await exportEntity(entityType, params);
  saveBlob(exported.blob, exported.filename);

  return exported;
}

/** Maps a failed export to the i18n key of a user facing message. */
export function getExportErrorKey(error: unknown) {
  const status = isAxiosError(error) ? error.response?.status : undefined;

  if (status === 403) return 'export.errors.forbidden';
  if (status === 404) return 'export.errors.notFound';

  return 'export.errors.generic';
}
